import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Button, Alert } from 'reactstrap'

import { useAuth } from '../../_helper/Auth/AuthProvider'

import { Breadcrumbs } from '../../AbstractElements'

import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

import { getIdData, addData, updateData } from '../../lib/counselorcorner'
import { getAllData as getAllInstructorData } from '../../lib/instructorinfo'
import { getAllData as getAllStudentData } from '../../lib/studentinfo'

const AdminPrivateNoteEdit = () => {
	const layout = useLayout()
	const role = useRoleBasedUrl()
	const { id } = useParams()
	const navigate = useNavigate()
	const { user } = useAuth()

	const [formData, setFormData] = useState({
		ndate: '',
		instructorId: '',
		studentId: '',
		note: ''
	})
	const [instructors, setInstructors] = useState([])
	const [students, setStudents] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const isAddMode = !id || id === 'create'
	const date = new Date(Date.now()).toLocaleDateString('en-CA')

	useEffect(() => {
		if (!isAddMode) {
			const fetchNoteData = async () => {
				try {
					const notesData = await getIdData(id)
					const note = notesData[0]
					setFormData({
						ndate: note.ndate,
						instructorId: note.instructorId,
						studentId: note.studentId,
						note: note.note
					})
				} catch (err) {
					setError('Failed to load note data')
					console.error('Error fetching note data:', err)
				}
			}
			fetchNoteData()
		} else {
			setFormData((prev) => ({ ...prev, ndate: date }))
		}
	}, [id, isAddMode, date])

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Fetch instructors
				const instructorsData = await getAllInstructorData()
				setInstructors(instructorsData)

				// Fetch students
				const studentsData = await getAllStudentData()
				setStudents(studentsData)
			} catch (err) {
				setError('Failed to load form data')
				console.error('Error fetching form data:', err)
			}
		}
		fetchData()
	}, [user.id])

	const handleInputChange = (e) => {
		const { name, value } = e.target
		setFormData((prev) => ({
			...prev,
			[name]: value
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		try {
			const taskData = isAddMode
				? {
						ndate: date,
						instructorId: formData.instructorId,
						studentId: formData.studentId,
						note: formData.note
				  }
				: {
						id,
						ndate: formData.ndate,
						instructorId: formData.instructorId,
						studentId: formData.studentId,
						note: formData.note
				  }

			if (isAddMode) {
				console.log('note added')
				await addData(taskData)
			} else {
				console.log('note updated')
				await updateData(taskData)
			}
			console.log('role', role)
			console.log('layout', layout)
			navigate(`${process.env.PUBLIC_URL}/${role}/private-notes/${layout}`)
		} catch (err) {
			setError(err.message || 'Failed to save note')
			console.error('Error saving note:', err)
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		console.log('handleCancel')
		console.log('role', role)
		console.log('layout', layout)
		navigate(`${process.env.PUBLIC_URL}/${role}/private-notes/${layout}`)
	}

	return (
		<Container fluid>
			<Breadcrumbs
				title={isAddMode ? 'Add Private Note' : 'Update Private Note'}
				mainTitle={isAddMode ? 'Add Private Note' : 'Update Private Note'}
				parent="Private Notes"
				url={`${process.env.PUBLIC_URL}/${role}/private-notes/${layout}`}
			/>

			<Row>
				<Col sm="12">
					<Card>
						<CardBody>
							<div className="personal-info-head mb-5">
								<h4>{isAddMode ? 'Add Private Note' : 'Update Private Note'}</h4>
								<p>Manage private notes for students and instructors</p>
							</div>

							{error && (
								<Alert color="danger" className="mb-4">
									{error}
								</Alert>
							)}

							<form onSubmit={handleSubmit}>
								<Row>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Date</Label>
											<input
												type="date"
												className="form-control"
												name="ndate"
												value={isAddMode ? date : formData.ndate}
												onChange={handleInputChange}
												required
											/>
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Written By</Label>
											<select className="form-control" name="instructorId" value={formData.instructorId} onChange={handleInputChange} required>
												<option value="">Select An Instructor</option>
												{instructors.map((data, ix) => (
													<option key={ix} value={data.id}>
														{data.name + ' ' + data.surname}
													</option>
												))}
											</select>
										</FormGroup>
									</Col>
								</Row>

								<Row>
									<Col md="12">
										<FormGroup>
											<Label className="form-control-label">Student</Label>
											<select className="form-control" name="studentId" value={formData.studentId} onChange={handleInputChange} required>
												<option value="">Select A Student</option>
												{students.map((data, ix) => (
													<option key={ix} value={data.id}>
														{data.name + ' ' + data.surname}
													</option>
												))}
											</select>
										</FormGroup>
									</Col>
								</Row>

								<Row>
									<Col md="12">
										<FormGroup>
											<Label className="form-control-label">Note</Label>
											<textarea
												name="note"
												rows={4}
												className="form-control"
												value={formData.note}
												onChange={handleInputChange}
												placeholder="Enter your private note here..."
												required
											/>
										</FormGroup>
									</Col>
								</Row>

								<div className="d-flex justify-content-between mt-4">
									<Button type="submit" color="primary" disabled={loading}>
										{loading ? 'Saving...' : isAddMode ? 'Add Note' : 'Update Note'}
									</Button>
									<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
										Cancel
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}

export { AdminPrivateNoteEdit }
